import type { PostDoc } from '../contract.js';
import { copy } from '../copy.js';
import { dateLong, monthKey, monthOf } from '../dates.js';
import { composeState, draftDirty, hasContent } from './derive.js';
import { entriesOf, isYou, mineFor, postedToday, todayOf } from './lookup.js';
import { fadeTo, homeNow } from './nav.js';
import { uiError } from './store.js';
import type { AppStore } from './store.js';
import type { Patch } from './types.js';

/** Writing, editing and deleting your post for today (03 §3.13, 04 §12). */

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const CLOSED: Patch = { sel: null, txt: null, tray: null, pdrag: null, dropCell: null };

/** Post today / Edit / Edit today: always opens today's post. */
export async function openCompose(s: AppStore): Promise<void> {
  let st = s.getState();
  if (st.lv || !st.session || st.view === 'compose') return;
  const kDate = todayOf(st);
  if (postedToday(st)) {
    if (!st.caps.editPost) return;
    // Your existing post is the starting point of the draft, so it has to be loaded first.
    await s.loadDay(kDate);
    st = s.getState();
    if (st.lv || st.view === 'compose' || st.days[kDate]?.status !== 'loaded') return;
  }
  const mine = mineFor(st, kDate);
  const draft: PostDoc = { items: mine ? clone(mine.items) : [], lines: mine?.lines ?? 'colour' };
  const base: Patch = {
    view: 'compose', kDate, draft, draftBase: JSON.stringify(draft), cStatus: 'idle', cAsk: null, cEditing: !!mine,
    cRet: st.view, cPerson: st.person, cDate: st.date, ...CLOSED, uploading: {}, play: null, pp: null, cd: null,
  };
  s.resetPointer();
  if (st.view === 'person' && isYou(st, st.person) && st.date === kDate) { s.clearLater(); s.stopPlayback(); s.set(base); return; }
  fadeTo(s, base);
}

/** Leave the composer for where it was opened from. */
export function closeCompose(s: AppStore, toast?: string): void {
  const st = s.getState(), ret = st.cRet;
  const back: Patch = { view: ret, draft: null, cStatus: 'idle', cAsk: null, ...CLOSED, uploading: {}, swap: 'still', pfx: null };
  if (ret === 'day' || ret === 'person') { back.date = st.cDate; back.person = st.cPerson; }
  let inPlace = false;
  if (ret === 'person') {
    const still = entriesOf(st, st.cDate).some((entry) => entry.memberId === st.cPerson);
    if (!still) { back.view = 'day'; back.person = null; }
    else inPlace = isYou(st, st.cPerson) && st.cDate === st.kDate;
  }
  s.resetPointer();
  if (inPlace) { s.clearLater(); s.set(back); }
  else fadeTo(s, back);
  if (toast) s.flash(toast);
}

/** Island Cancel: asks first when the draft has changed. */
export function cCancel(s: AppStore): void {
  const st = s.getState();
  if (st.view !== 'compose' || st.cStatus === 'saving') return;
  if (draftDirty(st)) { s.resetPointer(); s.set({ cAsk: 'discard', homeAfter: false, tray: null, sel: null, txt: null, pdrag: null }); return; }
  closeCompose(s);
}

/** Island Delete: always asks. */
export function askDelete(s: AppStore): void {
  if (s.getState().cStatus !== 'saving') s.set({ cAsk: 'delete', homeAfter: false, tray: null, sel: null, txt: null });
}

/** After a save or delete: show the result everywhere before leaving (06 §5.5). */
async function refreshToday(s: AppStore): Promise<void> {
  const date = s.getState().kDate;
  await Promise.all([s.loadDay(date, true), s.loadMonth(monthKey(monthOf(date)), true)]);
}

/** Share / Save / Try again. Waits for the backend's answer before leaving; nothing is optimistic. */
export async function cSave(s: AppStore): Promise<void> {
  const st = s.getState();
  if (st.view !== 'compose' || st.cStatus === 'saving' || !st.draft || !composeState(st).can) return;
  s.resetPointer();
  s.set({ cStatus: 'saving', cAsk: null, sel: null, txt: null, tray: null, pdrag: null });
  try {
    // Uploads started when files were added finish inside the normal "Saving…" state (04 §11.7).
    const failures = s.uploadFailures;
    await s.uploadsSettled();
    const now = s.getState();
    if (now.view !== 'compose' || !now.draft) return;
    if (s.uploadFailures !== failures) { s.set({ cStatus: 'failed' }); return; }
    const doc: PostDoc = { items: now.draft.items.filter(hasContent), lines: now.draft.lines || 'colour' };
    if (!doc.items.length) { s.set({ cStatus: 'failed' }); return; }
    const stored = await s.data.savePost(now.kDate, doc, { existing: now.cEditing });
    s.patchOwnPost(now.kDate, stored);
    await refreshToday(s);
    closeCompose(s, now.cEditing ? copy.toast.saved : copy.toast.shared(dateLong(now.kDate)));
  } catch (reason) {
    s.report(uiError(reason));
    if (s.getState().view === 'compose') s.set({ cStatus: 'failed' });
  }
}

/**
 * Delete, confirmed. The prototype deletes at once; a real delete takes a moment and can fail.
 * Implementation default (not from the prototype): the request runs in the "Saving…" state, and a
 * failure ends in the save-failure state with the draft kept.
 */
export async function cDelete(s: AppStore): Promise<void> {
  const st = s.getState();
  if (st.view !== 'compose' || st.cStatus === 'saving') return;
  s.set({ cStatus: 'saving', cAsk: null, sel: null, txt: null, tray: null, pdrag: null });
  try {
    await s.data.deletePost(st.kDate);
    s.patchOwnPost(st.kDate, null);
    await refreshToday(s);
    closeCompose(s, copy.toast.deleted);
  } catch (reason) {
    s.report(uiError(reason));
    if (s.getState().view === 'compose') s.set({ cStatus: 'failed' });
  }
}

/** Keep editing: closes the prompt. */
export function keepEditing(s: AppStore): void {
  if (s.getState().view === 'compose') s.set({ cAsk: null, homeAfter: false });
  else s.set({ peAsk: false, homeAfter: false });
}

/** The composer prompt's confirm button: Discard (leave, or go home) or Delete. */
export function confirmCompose(s: AppStore): void {
  const st = s.getState();
  if (st.homeAfter) { homeNow(s); return; }
  if (st.cAsk === 'delete') void cDelete(s);
  else closeCompose(s);
}
